import React, { useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { TransitionState } from 'gatsby-plugin-transition-link'

import useUIContext from 'context/ui'

import Layout from 'layouts'
import { Section } from 'components/layout'
import HorizontalScroll from 'components/layout/HorizontalScroll'
import SEO from 'components/seo'

import Intro from './components/Intro'
import Hero from './components/Hero'
import ImagesAndText from './components/ImagesAndText'
import Quote from './components/Quote'
import List from './components/List'
import Credits from './components/Credits'
import Cards from './components/Cards'
import HeroQuote from './components/HeroQuote'
import Awards from './components/Awards'
import RelatedProjects from './components/RelatedProjects'

const componentsByTypename = {}

const componentPairs = [
  ['Quote', Quote],
  ['List', List],
  ['Awards', Awards],
  ['Credits', Credits],
  ['Images_and_text', ImagesAndText],
  ['Cards_with_title', Cards],
  ['Hero_quote', HeroQuote],
]
const types = ['Project', 'Original']

componentPairs.forEach((pair) => {
  types.forEach((type) => {
    componentsByTypename[`PRISMIC_${type}_pageBody${pair[0]}`] = pair[1]
  })
})

const Slice = ({ typename, ...props }) => componentsByTypename?.[typename]?.(props) || null

Slice.propTypes = {
  typename: PropTypes.oneOf(Object.keys(componentsByTypename)),
}

const ProjectPage = ({ data }) => {
  const { page } = data.prismic
  const metaTitle = page?.page_meta_title
  const metaDescription = page?.page_meta_description
  const metaThumbnail = page?.page_meta_thumbnail

  const toggleHiddenHeader = useUIContext((s) => s.toggleHiddenHeader)
  const isHeaderHidden = useUIContext((s) => s.isHeaderHidden)

  const scrollbarRef = useRef(null)

  useEffect(() => {
    if (!scrollbarRef.current) return
    const onScroll = ({ offset }) => {
      if (isHeaderHidden && offset.x + window.innerWidth >= scrollbarRef.current.limit.x) {
        document.documentElement.classList.add('end-of-horizontal-scroll')
        toggleHiddenHeader(false)
      } else if (
        !isHeaderHidden &&
        offset.x + window.innerWidth < scrollbarRef.current.limit.x &&
        offset.x > window.innerWidth
      ) {
        document.documentElement.classList.remove('end-of-horizontal-scroll')
        toggleHiddenHeader(true)
      }
    }
    scrollbarRef.current.addListener(onScroll)

    return () => {
      scrollbarRef.current?.removeListener(onScroll)
    }
  }, [toggleHiddenHeader, isHeaderHidden])

  if (!page) return null
  return (
    <TransitionState>
      {({ mount, transitionStatus, current } = {}) => {
        return (
          <Layout footer={null}>
            <SEO title={metaTitle} description={metaDescription} thumbnail={metaThumbnail} />
            <HorizontalScroll>
              {({ scrollbar }) => {
                scrollbarRef.current = scrollbar
                return (
                  <>
                    <Section>
                      <Hero
                        embedId={page.embed_id || ''}
                        subtitle={page.subtitle || ''}
                        squareImage={page.square_image?.url || null}
                        squareVideoMP4={page.square_video_mp4 || null}
                        squareVideoWEBM={page.square_video_webm || null}
                        transitionStatus={transitionStatus}
                        scrollbar={scrollbar}
                      />
                    </Section>
                    <Section isHidden={transitionStatus === 'entering'}>
                      <Intro title={page.title} tagline={page.tagline} />
                    </Section>
                    {page?.body?.map(({ __typename, ...data }, index) => (
                      <Section isHidden={transitionStatus === 'entering'} key={index}>
                        <Slice typename={__typename} data={data} themeWaypoint={!index ? null : undefined} />
                      </Section>
                    ))}
                    <Section isHidden={transitionStatus === 'entering'}>
                      <RelatedProjects data={page?.related_projects} scrollbar={scrollbar} />
                    </Section>
                  </>
                )
              }}
            </HorizontalScroll>
          </Layout>
        )
      }}
    </TransitionState>
  )
}

export default ProjectPage
